import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContextLazy';
import { supabase } from '../../../lib/supabase-auth';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  ChatBubbleLeftEllipsisIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

// Types
interface Task {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  phase: number;
  owner_type: string;
  status: 'not_started' | 'in_progress' | 'blocked' | 'complete' | 'cancelled' | 'na';
  due_date: string | null;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  notes: string | null;
}

interface ProjectDocument {
  id: string;
  project_id: string;
  document_type: string;
  document_name: string;
  direction: 'from_client' | 'to_client';
  is_required: boolean;
  status: 'pending' | 'requested' | 'received' | 'reviewed' | 'approved' | 'rejected' | 'delivered' | 'na';
  file_path: string | null;
  file_name: string | null;
  file_size: number | null;
  uploaded_at: string | null;
  due_date: string | null;
  notes: string | null;
}

interface ProjectNote {
  id: string;
  note_type: string;
  subject: string | null;
  content: string;
  is_internal: boolean;
  occurred_at: string;
  creator?: {
    full_name: string;
  };
}

interface Project {
  id: string;
  project_number: string | null;
  name: string;
  description: string | null;
  status: string;
  current_phase: number;
  total_phases: number;
  tasks_total: number;
  tasks_completed: number;
  docs_required: number;
  docs_received: number;
  engaged_date: string | null;
  target_completion_date: string | null;
  assignee?: {
    id: string;
    full_name: string;
  };
}

type TabId = 'overview' | 'tasks' | 'documents' | 'updates';

const TABS: { id: TabId; label: string; icon: React.ComponentType<any> }[] = [
  { id: 'overview', label: 'Overview', icon: ClipboardDocumentListIcon },
  { id: 'tasks', label: 'Your Tasks', icon: CheckCircleIcon },
  { id: 'documents', label: 'Documents', icon: DocumentTextIcon },
  { id: 'updates', label: 'Updates', icon: ChatBubbleLeftEllipsisIcon },
];

// Default phases for projects
const DEFAULT_PHASES = [
  { number: 1, name: 'Discovery' },
  { number: 2, name: 'Analysis' },
  { number: 3, name: 'Strategy' },
  { number: 4, name: 'Implementation' },
  { number: 5, name: 'Review' },
];

export const ClientEngagementView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  // State
  const [project, setProject] = useState<Project | null>(null);
  const [clientPartyId, setClientPartyId] = useState<string | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Document upload state
  const [uploadingDocId, setUploadingDocId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(false);

  // Collapsible sections
  const [collapsedPhases, setCollapsedPhases] = useState<Set<number>>(new Set());
  const [expandedDocSection, setExpandedDocSection] = useState<'upload' | 'download' | null>('upload');

  // Get the user from auth context
  const { user } = useAuth();

  // Verify client has access to this project
  const verifyAccess = useCallback(async (): Promise<string | null> => {
    const userEmail = user?.email;
    if (!userEmail || !id) return null;

    // Find the party linked to this user
    const { data: partyData, error: partyError } = await supabase
      .from('parties')
      .select('id')
      .eq('primary_email', userEmail)
      .limit(1);

    if (partyError || !partyData?.[0]) return null;

    const partyId = partyData[0].id;

    // Verify this party has access to the project
    const { data, error: accessError } = await supabase
      .from('project_parties')
      .select('id')
      .eq('project_id', id)
      .eq('party_id', partyId)
      .limit(1);

    if (accessError || !data || data.length === 0) return null;

    return partyId;
  }, [user?.email, id]);

  // Fetch project data
  const fetchData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      // Verify access first - verifyAccess returns partyId if access granted
      const partyId = await verifyAccess();
      if (!partyId) {
        setError('You do not have access to this project.');
        setLoading(false);
        return;
      }
      setClientPartyId(partyId);

      // Fetch project
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('*, assignee:platform_users!assigned_to(id,full_name)')
        .eq('id', id)
        .single();

      if (projectError) throw projectError;
      setProject(projectData);

      // Fetch client's tasks only (owner_type = 'client')
      const { data: tasksData, error: tasksError } = await supabase
        .from('tasks')
        .select('*')
        .eq('project_id', id)
        .eq('owner_type', 'client')
        .order('phase', { ascending: true })
        .order('sort_order', { ascending: true });

      if (!tasksError) setTasks(tasksData || []);

      // Fetch documents
      const { data: docsData, error: docsError } = await supabase
        .from('project_documents')
        .select('*')
        .eq('project_id', id)
        .order('uploaded_at', { ascending: false });

      if (!docsError) setDocuments(docsData || []);

      // Fetch notes (non-internal only)
      const { data: notesData, error: notesError } = await supabase
        .from('project_notes')
        .select('*, creator:platform_users!created_by(full_name)')
        .eq('project_id', id)
        .eq('is_internal', false)
        .order('occurred_at', { ascending: false });

      if (!notesError) setNotes(notesData || []);

    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error fetching project:', err);
      }
      setError('Failed to load project data.');
    } finally {
      setLoading(false);
    }
  }, [id, verifyAccess]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update task status (client can mark their tasks complete)
  const updateTaskStatus = async (taskId: string, newStatus: Task['status']) => {
    if (!id) return;

    try {
      const { error } = await supabase
        .from('tasks')
        .update({
          status: newStatus,
          completed_at: newStatus === 'complete' ? new Date().toISOString() : null,
        })
        .eq('id', taskId);

      if (error) throw error;

      setTasks(prev => prev.map(t =>
        t.id === taskId ? { ...t, status: newStatus } : t
      ));
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error updating task:', err);
      }
    }
  };

  // Handle file upload
  const handleFileUpload = async (docId: string, file: File) => {
    if (!id || !clientPartyId) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a PDF, image, or Office document.');
      return;
    }

    setUploadProgress(true);
    setUploadingDocId(docId);

    try {
      const filePath = `${id}/${docId}/${file.name}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('project-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Update document record
      const { error: updateError } = await supabase
        .from('project_documents')
        .update({
          file_path: filePath,
          file_name: file.name,
          file_size: file.size,
          uploaded_at: new Date().toISOString(),
          uploaded_by_party_id: clientPartyId,
          status: 'received',
        })
        .eq('id', docId);

      if (updateError) throw updateError;

      // Refresh documents
      fetchData();

    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error uploading file:', err);
      }
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadProgress(false);
      setUploadingDocId(null);
    }
  };

  // Download document
  const downloadDocument = async (doc: ProjectDocument) => {
    if (!doc.file_path) return;

    try {
      const { data, error } = await supabase.storage
        .from('project-documents')
        .createSignedUrl(doc.file_path, 60 * 60);

      if (error) throw error;
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error downloading:', err);
      }
    }
  };

  // Status badge
  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const config: Record<string, { bg: string; text: string; label: string }> = {
      engaged: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Getting Started' },
      in_progress: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'In Progress' },
      on_hold: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'On Hold' },
      complete: { bg: 'bg-green-100', text: 'text-green-800', label: 'Complete' },
    };
    const { bg, text, label } = config[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}>
        {label}
      </span>
    );
  };

  // Progress Stepper
  const ProgressStepper: React.FC = () => {
    const phases = DEFAULT_PHASES.slice(0, project?.total_phases || 5);
    const currentPhase = project?.current_phase || 1;

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-900 mb-4">Project Progress</h3>
        <div className="relative">
          {/* Progress line */}
          <div className="absolute top-4 left-4 right-4 h-0.5 bg-gray-200">
            <div
              className="h-full bg-blue-600 transition-all duration-500"
              style={{ width: `${((currentPhase - 1) / Math.max(phases.length - 1, 1)) * 100}%` }}
            />
          </div>

          {/* Phase markers */}
          <div className="relative flex justify-between">
            {phases.map((phase) => {
              const isComplete = phase.number < currentPhase;
              const isCurrent = phase.number === currentPhase;

              return (
                <div key={phase.number} className="flex flex-col items-center">
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all
                      ${isComplete ? 'bg-blue-600 text-white' : ''}
                      ${isCurrent ? 'bg-blue-600 text-white ring-4 ring-blue-100' : ''}
                      ${!isComplete && !isCurrent ? 'bg-white border-2 border-gray-300 text-gray-500' : ''}
                    `}
                  >
                    {isComplete ? <CheckCircleIcon className="h-5 w-5" /> : phase.number}
                  </div>
                  <span className={`mt-2 text-xs font-medium text-center max-w-[80px] ${isCurrent ? 'text-blue-600' : 'text-gray-500'}`}>
                    {phase.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="flex items-center gap-3 text-gray-500">
          <ArrowPathIcon className="h-6 w-6 animate-spin" />
          <span>Loading project...</span>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <ExclamationCircleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-900 mb-2">Unable to Load Project</h3>
          <p className="text-red-700">{error || 'Project not found.'}</p>
          <Link
            to="/portal/client/dashboard"
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const taskProgress = project.tasks_total > 0
    ? Math.round((project.tasks_completed / project.tasks_total) * 100)
    : 0;

  // Filter documents by direction
  const docsToUpload = documents.filter(d => d.direction === 'from_client' && d.status !== 'na');
  const docsToDownload = documents.filter(d => d.direction === 'to_client' && d.status !== 'na');
  const pendingUploads = docsToUpload.filter(d => !['received', 'reviewed', 'approved'].includes(d.status));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Back Link */}
      <Link
        to="/portal/client/dashboard"
        className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Dashboard
      </Link>

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {project.project_number && (
                <span className="text-xs font-mono text-gray-500">{project.project_number}</span>
              )}
              <StatusBadge status={project.status} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
            {project.description && (
              <p className="text-gray-600 mt-2">{project.description}</p>
            )}
          </div>

          <div className="flex items-center gap-6 text-sm">
            {project.assignee && (
              <div>
                <span className="text-gray-500">Your Advisor</span>
                <p className="font-medium text-gray-900">{project.assignee.full_name}</p>
              </div>
            )}
            {project.target_completion_date && (
              <div>
                <span className="text-gray-500">Target Date</span>
                <p className="font-medium text-gray-900">
                  {new Date(project.target_completion_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Overall Progress */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Overall Progress</span>
            <span className="text-sm font-bold text-gray-900">{taskProgress}%</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-500"
              style={{ width: `${taskProgress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {project.tasks_completed} of {project.tasks_total} tasks complete
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors
                    ${isActive
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                  {tab.label}
                  {tab.id === 'tasks' && tasks.filter(t => t.status !== 'complete').length > 0 && (
                    <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                      {tasks.filter(t => t.status !== 'complete').length}
                    </span>
                  )}
                  {tab.id === 'documents' && pendingUploads.length > 0 && (
                    <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
                      {pendingUploads.length}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <ProgressStepper />

              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircleIcon className="h-8 w-8 text-blue-600" />
                    <div>
                      <p className="text-sm text-blue-700">Your Tasks</p>
                      <p className="text-xl font-bold text-blue-900">
                        {tasks.filter(t => t.status === 'complete').length}/{tasks.length}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <DocumentTextIcon className="h-8 w-8 text-purple-600" />
                    <div>
                      <p className="text-sm text-purple-700">Documents</p>
                      <p className="text-xl font-bold text-purple-900">
                        {docsToUpload.filter(d => ['received', 'reviewed', 'approved'].includes(d.status)).length}/{docsToUpload.length}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <ClockIcon className="h-8 w-8 text-green-600" />
                    <div>
                      <p className="text-sm text-green-700">Phase</p>
                      <p className="text-xl font-bold text-green-900">
                        {project.current_phase} of {project.total_phases}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Next Actions Preview */}
              {tasks.filter(t => t.status !== 'complete').length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-medium text-amber-900 mb-2">What You Need to Do</h4>
                  <ul className="space-y-2">
                    {tasks.filter(t => t.status !== 'complete').slice(0, 3).map(task => (
                      <li key={task.id} className="flex items-center gap-2 text-sm text-amber-800">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-600" />
                        {task.title}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setActiveTab('tasks')}
                    className="text-sm text-amber-700 font-medium mt-3 hover:text-amber-900"
                  >
                    View all tasks
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tasks Tab */}
          {activeTab === 'tasks' && (
            <div className="space-y-4">
              {tasks.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircleSolidIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks Assigned to You</h3>
                  <p className="text-gray-500">Your advisor will assign tasks when needed.</p>
                </div>
              ) : (
                <>
                  {/* Group by phase */}
                  {Array.from(new Set(tasks.map(t => t.phase))).sort().map(phase => {
                    const phaseTasks = tasks.filter(t => t.phase === phase);
                    const completedCount = phaseTasks.filter(t => t.status === 'complete').length;
                    const isCollapsed = collapsedPhases.has(phase);
                    const phaseName = DEFAULT_PHASES.find(p => p.number === phase)?.name || `Phase ${phase}`;

                    return (
                      <div key={phase} className="border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setCollapsedPhases(prev => {
                            const next = new Set(prev);
                            if (next.has(phase)) {
                              next.delete(phase);
                            } else {
                              next.add(phase);
                            }
                            return next;
                          })}
                          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-medium text-sm">
                              {phase}
                            </span>
                            <span className="font-medium text-gray-900">{phaseName}</span>
                            <span className="text-sm text-gray-500">
                              {completedCount}/{phaseTasks.length} complete
                            </span>
                          </div>
                          {isCollapsed ? (
                            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>

                        {!isCollapsed && (
                          <div className="divide-y divide-gray-100">
                            {phaseTasks.map(task => {
                              const isComplete = task.status === 'complete';
                              const isOverdue = task.due_date && new Date(task.due_date) < new Date() && !isComplete;

                              return (
                                <div
                                  key={task.id}
                                  className={`p-4 flex items-start gap-4 ${isComplete ? 'bg-gray-50' : ''}`}
                                >
                                  <button
                                    onClick={() => updateTaskStatus(task.id, isComplete ? 'not_started' : 'complete')}
                                    className={`
                                      flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors
                                      ${isComplete
                                        ? 'bg-green-500 border-green-500 text-white'
                                        : 'border-gray-300 hover:border-green-500'
                                      }
                                    `}
                                  >
                                    {isComplete && <CheckCircleIcon className="h-4 w-4" />}
                                  </button>

                                  <div className="flex-1 min-w-0">
                                    <p className={`font-medium ${isComplete ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                      {task.title}
                                    </p>
                                    {task.description && (
                                      <p className="text-sm text-gray-500 mt-1">{task.description}</p>
                                    )}
                                    {task.due_date && (
                                      <div className={`flex items-center gap-1 mt-2 text-xs ${isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
                                        <CalendarDaysIcon className="h-4 w-4" />
                                        <span>{isOverdue && 'Overdue: '}{new Date(task.due_date).toLocaleDateString()}</span>
                                      </div>
                                    )}
                                  </div>

                                  {task.priority === 'urgent' && (
                                    <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                                      Urgent
                                    </span>
                                  )}
                                  {task.priority === 'high' && (
                                    <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded">
                                      High Priority
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              {/* Documents to Upload */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedDocSection(expandedDocSection === 'upload' ? null : 'upload')}
                  className="w-full flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <ArrowUpTrayIcon className="h-5 w-5 text-purple-600" />
                    <span className="font-medium text-purple-900">Documents to Upload</span>
                    <span className="text-sm text-purple-700">
                      {docsToUpload.filter(d => ['received', 'reviewed', 'approved'].includes(d.status)).length}/{docsToUpload.length}
                    </span>
                  </div>
                  {expandedDocSection === 'upload' ? (
                    <ChevronUpIcon className="h-5 w-5 text-purple-600" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-purple-600" />
                  )}
                </button>

                {expandedDocSection === 'upload' && (
                  <div className="divide-y divide-gray-100">
                    <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500">
                      Accepted formats: PDF, Word, Excel, CSV, images (max 10MB)
                    </div>
                    {docsToUpload.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        No documents requested yet.
                      </div>
                    ) : (
                      docsToUpload.map(doc => {
                        const isUploaded = ['received', 'reviewed', 'approved'].includes(doc.status);
                        const isUploading = uploadingDocId === doc.id;

                        return (
                          <div key={doc.id} className="p-4 flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900">{doc.document_name}</p>
                                {doc.is_required && (
                                  <span className="text-xs text-red-600">Required</span>
                                )}
                              </div>
                              {doc.notes && (
                                <p className="text-sm text-gray-500 mt-1">{doc.notes}</p>
                              )}
                              {doc.due_date && (
                                <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                                  <CalendarDaysIcon className="h-3 w-3" />
                                  Due: {new Date(doc.due_date).toLocaleDateString()}
                                </div>
                              )}
                            </div>

                            <div className="flex-shrink-0">
                              {isUploaded ? (
                                <div className="flex items-center gap-2 text-green-600">
                                  <CheckCircleSolidIcon className="h-5 w-5" />
                                  <span className="text-sm">Uploaded</span>
                                </div>
                              ) : (
                                <label className="cursor-pointer">
                                  <input
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleFileUpload(doc.id, file);
                                    }}
                                    disabled={isUploading}
                                  />
                                  <span className={`
                                    inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                                    ${isUploading
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                    }
                                  `}>
                                    {isUploading ? (
                                      <>
                                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                        Uploading...
                                      </>
                                    ) : (
                                      <>
                                        <ArrowUpTrayIcon className="h-4 w-4" />
                                        Upload
                                      </>
                                    )}
                                  </span>
                                </label>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* Documents to Download */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedDocSection(expandedDocSection === 'download' ? null : 'download')}
                  className="w-full flex items-center justify-between p-4 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <ArrowDownTrayIcon className="h-5 w-5 text-emerald-600" />
                    <span className="font-medium text-emerald-900">Documents for You</span>
                    <span className="text-sm text-emerald-700">{docsToDownload.length} available</span>
                  </div>
                  {expandedDocSection === 'download' ? (
                    <ChevronUpIcon className="h-5 w-5 text-emerald-600" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-emerald-600" />
                  )}
                </button>

                {expandedDocSection === 'download' && (
                  <div className="divide-y divide-gray-100">
                    {docsToDownload.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        No documents available for download yet.
                      </div>
                    ) : (
                      docsToDownload.map(doc => (
                        <div key={doc.id} className="p-4 flex items-center justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">{doc.document_name}</p>
                            {doc.file_name && (
                              <p className="text-sm text-gray-500 mt-1">{doc.file_name}</p>
                            )}
                            {doc.uploaded_at && (
                              <p className="text-xs text-gray-400 mt-1">
                                Added {new Date(doc.uploaded_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>

                          {doc.file_path ? (
                            <button
                              onClick={() => downloadDocument(doc)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-200 transition-colors"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                              Download
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400">Preparing...</span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Updates Tab */}
          {activeTab === 'updates' && (
            <div className="space-y-4">
              {notes.length === 0 ? (
                <div className="text-center py-12">
                  <ChatBubbleLeftEllipsisIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Updates Yet</h3>
                  <p className="text-gray-500">Your advisor will post updates as your project progresses.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notes.map(note => {
                    const typeConfig: Record<string, { bg: string; text: string; label: string }> = {
                      meeting: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Meeting' },
                      call: { bg: 'bg-green-100', text: 'text-green-700', label: 'Call' },
                      email: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Email' },
                      decision: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Decision' },
                      milestone: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Milestone' },
                      update: { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'Update' },
                    };
                    const config = typeConfig[note.note_type] || { bg: 'bg-gray-100', text: 'text-gray-700', label: note.note_type };

                    return (
                      <div key={note.id} className="py-4">
                        <div className="flex items-start gap-4">
                          <div className={`flex-shrink-0 px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
                            {config.label}
                          </div>
                          <div className="flex-1 min-w-0">
                            {note.subject && (
                              <p className="font-medium text-gray-900">{note.subject}</p>
                            )}
                            <p className="text-gray-600 mt-1 whitespace-pre-wrap">{note.content}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                              <span>{note.creator?.full_name || 'Advisor'}</span>
                              <span>|</span>
                              <span>{new Date(note.occurred_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientEngagementView;
